import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { State, process } from '@progress/kendo-data-query';
import { formatNumber } from '@progress/kendo-intl';
import { DialogActionsBar } from '@progress/kendo-react-dialogs';
import {
  GridDataStateChangeEvent,
  GridHeaderSelectionChangeEvent,
  GridItemChangeEvent,
  GridPagerSettings,
  GridSelectionChangeEvent,
} from '@progress/kendo-react-grid';
import { GridSelectableSettings } from '@progress/kendo-react-grid/dist/npm/interfaces/GridSelectableSettings';
import { FC, useCallback, useMemo, useState } from 'react';
import {
  EditableColumnCategories,
  viewCheckbookCategories,
} from 'src/assets/static/view_checkbook/useCheckbookCategories';
import useCheckbookPeriodColumns from 'src/assets/static/view_checkbook/useCheckbookPeriodColumns';
import Button from 'src/components/design-system/Button';
import Dialog from 'src/components/design-system/Dialog';
import GridWrapper from 'src/components/GridWrapper';
import { CanUsesInitialBudgets } from 'src/constants/permissionsList';
import useBoolean from 'src/hooks/useBoolean';
import usePeriodView from 'src/hooks/usePeriodView';
import usePermission from 'src/hooks/usePermissions';
import useViewCheckbook from 'src/hooks/useViewCheckbook';
import useWindowSize from 'src/hooks/useWindowSize';
import { ClientResponse } from 'src/services/company/useGetClientsQuery';
import { GLCodePeriodDepartment } from 'src/services/useGlCodeDepartmentApi';
import { GLCodePeriodBudgetResponse } from 'src/services/useGlCodePeriodBudgetApi';
import { IFilters } from 'src/types/PostBody';
import { budgetPercentageCal, isNullOrUndefined } from 'src/utils';
import exportToExcel from 'src/utils/exportToExcel';
import { round } from 'src/utils/number';
import { modifyFilters } from 'src/utils/requestInterceptor';
import { sliceFiveChar } from 'src/utils/strings';
import { GLPeriodDropdownChildren } from 'src/utils/transformation';
import CheckbookActualizedAmountTable from './CheckbookActualizedAmountTable';
import CheckbookCommittedAmountTable from './CheckbookCommittedAmountTable';
import CheckbookHoldAmountTable, { checkbookBalanceTitle } from './CheckbookHoldAmountTable';
import DepartmentsTable from './DepartmentsTable';
import GLCodeApprovalAssignedUsersTableToolbar from './GLCodeApprovalAssignedUsersTableToolbar';
import ManageGLCodeAttributesPanel from './period_view/ManageGLCodeAtrSidePanel/ManageGLCodeAttributesPanel';
import PeriodFilterPills from './PeriodFilterPills';
import TransactionTable from './TransactionTable';
import ViewCheckbookActions from './ViewCheckbookActions';
import ViewCheckbookPeriodTableToolbar from './ViewCheckbookPeriodTableToolbar';

const selectable: GridSelectableSettings = {
  enabled: false,
  drag: false,
  cell: false,
};

const gridPagerSettings: GridPagerSettings = {
  buttonCount: 4,
  pageSizes: [10, 20, 50, 100, 5000],
};

const assignedUserGridPagerSettings: GridPagerSettings = {
  buttonCount: 1,
  pageSizes: [10, 20, 50],
};

const defaultProps = {
  excelFileName: 'View Checkbook.xlsx',
  tableTextId: 'GL Codes',
  DataItemKey: 'glCode',
  sortable: true,
  resizable: true,
  showColumnMenu: true,
  selectable: selectable,
  pageable: gridPagerSettings,
} as const;

const assignedUsersDefaultProps = {
  excelFileName: 'View Checkbook.xlsx',
  tableTextId: 'Contacts',
  DataItemKey: 'users',
  sortable: true,
  resizable: true,
  showColumnMenu: true,
  pageable: assignedUserGridPagerSettings,
} as const;

interface ViewPeriodCheckbookTableProps {}

export const CHECKBOOK_PERIOD_SELECTION_FILTER_INITIAL_STATE = viewCheckbookCategories[2];

export const PERIOD_DATE_FIELDS = ['statusDate', 'poDate'];
export const PERIOD_STRING_FIELDS = [
  'poNum',
  'invoiceNumber',
  'status',
  'supplierName',
  'poStatus',
  'hasGlMismatchItems',
  'poItemsCompleted',
  'poSubject',
  'buyerName',
];

export const isColumnVisible = (editableColumns: EditableColumnCategories[], field: string) => {
  const column = editableColumns.find((c) => c.field === field);
  return column ? column.show : true;
};

const initialDataState: State = {
  skip: 0,
  take: 10,
  sort: [{ field: 'name', dir: 'asc' }],
};

const ViewPeriodCheckbookTable: FC<ViewPeriodCheckbookTableProps> = () => {
  const {
    glCodePeriodBudget,
    setGlCodePeriodBudget,
    periodCheckbookDataState,
    setPeriodCheckbookDataState,
    isGlCodesBudgetLoading,
    glPeriodIdByPeriod,
    selectedPeriodDepartment,
    dropDownPeriodValue,
    balanceType,
    setBalanceType,
    setToggleExpandFalse,
    isEditMode,
    selectedCompany,
    t,
    selectedView,
    confirmBeforeProceeding,
    setEditingGlCodePeriodBudget,
    editingbudgetPeriodDataWithErrors,
    buId,
    openUsersAssigned,
    setOpenUsersAssigned,
    assignedUsers,
  } = useViewCheckbook();

  const { getPeriodFilters, periodViewData, periodViewTotal, getPeriodSorts } = usePeriodView();
  const { editableColumnDropdownData } = usePeriodView();

  const {
    value: openBalanceActivityModal,
    toggle: toggleBalanceActivityModal,
    setTrue: setOpenBalanceActivityModal,
  } = useBoolean();

  const [departments, setDepartments] = useState<GLCodePeriodDepartment[]>([]);
  const { width, height } = useWindowSize();
  const [excelLoading, setExcelLoading] = useState<boolean>(false);
  const [dataState, setDataState] = useState(initialDataState);

  const { hasTransactionData } = dropDownPeriodValue || ({} as GLPeriodDropdownChildren);
  const [selectedGLBudget, setSelectedGLBudget] = useState<GLCodePeriodBudgetResponse>(
    {} as GLCodePeriodBudgetResponse
  );
  const isColumnMenuDisabled = useMemo(() => {
    const filters: IFilters[] = modifyFilters(periodCheckbookDataState.filter);
    const isDefaultFilter =
      filters.length <= 2 &&
      filters.every((item) => item.field === 'isDeleted' || item.field === 'isActive');
    return !glPeriodIdByPeriod
      ? true
      : !!(glPeriodIdByPeriod && glCodePeriodBudget.length === 0 && isDefaultFilter);
  }, [periodCheckbookDataState.filter, glCodePeriodBudget.length, glPeriodIdByPeriod]);

  const viewPeriodCheckbookColumns = useCheckbookPeriodColumns(isEditMode, isColumnMenuDisabled);
  const usesBudgets = usePermission({ permissions: CanUsesInitialBudgets, companyAttribute: true });

  const periodViewColumns = useMemo(() => {
    const column = viewPeriodCheckbookColumns.map((item) => {
      if (item.childrenColumn) {
        const hideParentColumn = item.childrenColumn.every(
          (col) => isColumnVisible(editableColumnDropdownData, col.field as string) === false
        );

        return {
          ...item,
          show: hideParentColumn ? false : true,
          childrenColumn: item.childrenColumn?.map((column) => ({
            ...column,
            show: isColumnVisible(editableColumnDropdownData, column.field || ''),
          })),
        };
      }
      return { ...item, show: isColumnVisible(editableColumnDropdownData, item.field || '') };
    });
    return column;
  }, [editableColumnDropdownData, viewPeriodCheckbookColumns]);

  const viewCheckbookExcelColumns = useMemo(() => {
    const flattenedColumns = viewPeriodCheckbookColumns.reduce((acc: any, item: any) => {
      if (item.childrenColumn) {
        return [...acc, item, ...item.childrenColumn];
      }
      return [...acc, item];
    }, []);

    const updatedColumns = flattenedColumns.map((item) => {
      switch (item.field) {
        case 'remainingInitialBudgetAmount':
          return { ...item, title: `$ ${t('Remaining Budget')}` };
        case 'remainingInitialBudgetAmountPercentage':
          return { ...item, title: `% ${t('Remaining Budget')}` };
        case 'remainingBudgetAmount':
          return { ...item, title: `$ ${t('Remaining Forecast')}` };
        case 'remainingBudgetAmountPercentage':
          return { ...item, title: `% ${t('Remaining Forecast')}` };
        case 'committedAmount':
          return { ...item, title: t('Committed Amt') };
        case 'budgetAmount':
          return { ...item, title: t('Forecast Amt') };
        case 'holdAmount':
          return { ...item, title: t('Planned Amt') };
        case 'spentAmount':
          return { ...item, title: t('Actualized Amt') };
        case 'poApproval':
          return { ...item, title: t('PO Approval Exceptions') };
        case 'invoiceApproval':
          return { ...item, title: t('Invoice Approval Exceptions') };
        default:
          return item;
      }
    });

    return updatedColumns.filter(
      (item) =>
        ![
          'selected',
          'hasErrorsOrWarnings',
          'departments',
          'transaction',
          'Forecast',
          'journalEntry',
          'budget',
          'forecast',
          'errors',
          ...(!usesBudgets
            ? [
                'initialBudget',
                'remainingInitialBudgetAmount',
                'remainingInitialBudgetAmountPercentage',
              ]
            : []),
        ].includes(item?.field || '')
    );
  }, [t, usesBudgets, viewPeriodCheckbookColumns]);

  const gridHeight: number = useMemo(
    () => (dropDownPeriodValue && !hasTransactionData ? height - 240 : height - 200),
    [dropDownPeriodValue, hasTransactionData, height]
  );

  const excelExport = useCallback(
    (data: GLCodePeriodBudgetResponse[], isDepartmentIncluded: boolean = false) => {
      const columns: string[] = [];
      const columnMapping: Record<string, string> = {};
      const checkbookColumns = [
        ...(isDepartmentIncluded ? [{ field: 'department', title: t('Department') }] : []),
        { field: 'companyName', title: t('Company Name') },

        ...viewCheckbookExcelColumns,
      ];

      checkbookColumns.forEach((item) => {
        if (item.field !== '') {
          columns.push(item.field as string);
          Object.assign(columnMapping, { [item.field as string]: item.title });
        }
      });

      const rows = data.map((item) => ({
        ...item,
        holdAmount: formatNumber(item.holdAmount, 'c2'),
        spentAmount: formatNumber(item.spentAmount, 'c2'),
        budgetAmount: formatNumber(item.budgetAmount, 'c2'),
        committedAmount: formatNumber(Number(item?.committedAmount), 'c2'),
        remainingBudgetAmount: formatNumber(item.remainingBudgetAmount, 'c2'),
        remainingBudgetAmountPercentage: `${item.remainingBudgetAmountPercentage}%`,
        initialBudget:
          item.initialBudget !== null ? formatNumber(Number(item.initialBudget), 'c2') : '',
        remainingInitialBudgetAmount:
          item.initialBudget !== null
            ? formatNumber(Number(item.remainingInitialBudgetAmount), 'c2')
            : '',
        remainingInitialBudgetAmountPercentage:
          item.initialBudget !== null ? `${item.remainingInitialBudgetAmountPercentage}%` : '0.00%',
        isActive: item.isActive ? 'Yes' : 'No',
        isFixed: item.isFixed ? 'Yes' : 'No',
      }));
      exportToExcel({
        columns,
        rows,
        fileName: `${buId ? `${buId}_` : ''}${
          sliceFiveChar(selectedCompany?.companyName || '') + '_'
        }${`FY${dropDownPeriodValue?.fiscalYear || ''}${
          selectedView === 0 ? `P${dropDownPeriodValue?.period?.trim() || ''}_` : ''
        }`}${
          selectedPeriodDepartment?.departmentName !== 'All Departments'
            ? `${sliceFiveChar(selectedPeriodDepartment.departmentName || '')}_`
            : ''
        }${new Date().toLocaleString().replace(/[ ,]+/g, ' ')}`,
        columnMapping,
        sheetName: t(`FY${dropDownPeriodValue?.fiscalYear}P${dropDownPeriodValue?.period?.trim()}`),
      });
    },
    [
      buId,
      dropDownPeriodValue?.fiscalYear,
      dropDownPeriodValue?.period,
      selectedCompany?.companyName,
      selectedPeriodDepartment.departmentName,
      selectedView,
      t,
      viewCheckbookExcelColumns,
    ]
  );

  const assignedUsersExcelExport = useCallback((data: ClientResponse[]) => {
    const columns: string[] = [];
    const columnMapping: Record<string, string> = {};
    [
      { field: 'glCode', title: 'GL Code' },
      { field: 'description', title: 'Description' },
      { field: 'status', title: 'Invoice/PO Approval Attribute' },
      { field: 'name', title: 'Contact Assigned' },
    ].forEach((item) => {
      columns.push(item.field as string);
      Object.assign(columnMapping, { [item.field as string]: item.title });
    });

    exportToExcel({
      columns,
      rows: data,
      fileName: 'Assigned Contacts',
      columnMapping,
    });
  }, []);

  const changeDataState = useCallback(
    (dataState: State) => {
      setGlCodePeriodBudget((periods) => periods.map((glCode) => ({ ...glCode, selected: false })));
      setPeriodCheckbookDataState(dataState);
    },
    [setGlCodePeriodBudget, setPeriodCheckbookDataState]
  );

  const onCheckbookDataStateChange = useCallback(
    (e: GridDataStateChangeEvent) => {
      const { dataState } = e;
      confirmBeforeProceeding(() => {
        changeDataState(dataState);
      });
    },
    [changeDataState, confirmBeforeProceeding]
  );

  const handleCellFnClick = useCallback(
    ({
      data,
      type,
    }: {
      data: any;
      type:
        | 'holdAmount'
        | 'actualizedAmount'
        | 'committedAmount'
        | 'journalEntry'
        | 'logAmount'
        | 'departments';
    }) => {
      if (
        type === 'holdAmount' ||
        type === 'actualizedAmount' ||
        type === 'committedAmount' ||
        type === 'logAmount' ||
        type === 'departments'
      ) {
        setBalanceType(type);
        if (type === 'departments') {
          setDepartments(data);
        } else {
          setSelectedGLBudget(data);
        }
        setOpenBalanceActivityModal();
      }
    },
    [setBalanceType, setOpenBalanceActivityModal]
  );

  const handleBalanceActivityModalClose = useCallback(() => {
    toggleBalanceActivityModal();
    setToggleExpandFalse();
  }, [setToggleExpandFalse, toggleBalanceActivityModal]);

  const handleAssignedUsersClose = useCallback(() => {
    setOpenUsersAssigned(false);
    setDataState(initialDataState);
  }, [setOpenUsersAssigned]);
  const onHeaderSelectionChange = useCallback(
    (event: GridHeaderSelectionChangeEvent) => {
      const checkboxElement: any = event?.syntheticEvent?.target;
      const { checked } = checkboxElement;
      if (isEditMode) {
        setEditingGlCodePeriodBudget((prev) => prev?.map((row) => ({ ...row, selected: checked })));
      } else {
        setGlCodePeriodBudget((prev) => prev?.map((row) => ({ ...row, selected: checked })));
      }
    },
    [isEditMode, setEditingGlCodePeriodBudget, setGlCodePeriodBudget]
  );

  const onCheckbookSelectionChange = useCallback(
    (event: GridSelectionChangeEvent) => {
      if (isEditMode) {
        setEditingGlCodePeriodBudget((prev) => {
          const prevItems = [...prev].map((item) =>
            item.rowId === event?.dataItem.rowId ? { ...item, selected: !item.selected } : item
          );
          return prevItems;
        });
      } else {
        setGlCodePeriodBudget((prev) => {
          const prevItems = [...prev].map((item) =>
            item.rowId === event?.dataItem.rowId ? { ...item, selected: !item.selected } : item
          );
          return prevItems;
        });
      }
    },
    [isEditMode, setEditingGlCodePeriodBudget, setGlCodePeriodBudget]
  );

  const handleItemChange = useCallback(
    (e: GridItemChangeEvent) => {
      const { field = '', value } = e;
      const { rowId } = e.dataItem || {};

      if (field === 'budgetAmount') {
        setEditingGlCodePeriodBudget((prev: any) =>
          prev.map((item: any) => {
            if (rowId === item.rowId) {
              const newValue = value;
              const remainingBudgetAmount = round(
                newValue - (item.spentAmount || 0) - (item.holdAmount || 0),
                2
              );

              const remainingBudgetAmountPercentage = budgetPercentageCal(
                remainingBudgetAmount,
                newValue
              );

              return {
                ...item,
                [field]: isNullOrUndefined(newValue) ? 0 : newValue?.toString(),
                remainingBudgetAmount: remainingBudgetAmount,
                remainingBudgetAmountPercentage: remainingBudgetAmountPercentage,
              };
            }
            return item;
          })
        );
      } else if (field === 'initialBudget') {
        setEditingGlCodePeriodBudget((prev: any) =>
          prev.map((item: any) => {
            if (rowId === item.rowId) {
              const spentAmount = item.spentAmount || 0;
              const holdAmount = item.holdAmount || 0;
              const remainingInitialBudgetAmount = round(value - spentAmount - holdAmount, 2);

              const remainingInitialBudgetAmountPercentage = budgetPercentageCal(
                remainingInitialBudgetAmount,
                value
              );

              return {
                ...item,
                initialBudget: value === null || value === '' ? '' : String(value),
                remainingInitialBudgetAmount:
                  value === null || value === '' ? '' : remainingInitialBudgetAmount,
                remainingInitialBudgetAmountPercentage:
                  value === null || value === '' ? '' : remainingInitialBudgetAmountPercentage,
              };
            }
            return item;
          })
        );
      } else {
        setEditingGlCodePeriodBudget((prev) => [
          ...prev.map((item) => ({
            ...item,
            ...(rowId === item.rowId && {
              [field]: value,
            }),
          })),
        ]);
      }
    },
    [setEditingGlCodePeriodBudget]
  );

  const gridProps = useMemo(
    () =>
      width <= 1080
        ? {
            ...defaultProps,
            pageable: { ...defaultProps.pageable, buttonCount: 1 },
            sortable: isEditMode ? false : true,
          }
        : defaultProps,
    [isEditMode, width]
  );
  const assignedUsersGridProps = useMemo(
    () =>
      width <= 1080
        ? {
            ...assignedUsersDefaultProps,
            pageable: { ...assignedUsersDefaultProps.pageable, buttonCount: 1 },
            sortable: isEditMode ? false : true,
          }
        : assignedUsersDefaultProps,
    [isEditMode, width]
  );
  const { data: allPageData } = useMemo(
    () =>
      process(glCodePeriodBudget, {
        filter: {
          logic: 'and',
          filters: getPeriodFilters(periodCheckbookDataState),
        },
        sort: getPeriodSorts(periodCheckbookDataState),
      }) as {
        data: GLCodePeriodBudgetResponse[];
        total: number;
      },
    [glCodePeriodBudget, getPeriodFilters, periodCheckbookDataState, getPeriodSorts]
  );

  const gridToolbarChildren = useMemo(
    () => (
      <ViewCheckbookPeriodTableToolbar
        allPageData={allPageData}
        excelExport={excelExport}
        rows={periodViewData}
        setLoading={setExcelLoading}
        setState={setPeriodCheckbookDataState}
      />
    ),
    [allPageData, excelExport, periodViewData, setPeriodCheckbookDataState]
  );

  const { data: assignedUsersData, total } = useMemo(
    () => process(assignedUsers.users || [], dataState),
    [assignedUsers.users, dataState]
  );

  const onDataStateChange = (e: GridDataStateChangeEvent) => {
    setDataState(e.dataState);
  };

  const handleClearFilter = useCallback(() => {
    setDataState((prev) => ({
      ...initialDataState,
      skip: prev.skip,
      take: prev.take,
    }));
  }, []);

  const assignedUsersGridToolbarChildren = useMemo(
    () => (
      <GLCodeApprovalAssignedUsersTableToolbar
        allPageData={assignedUsers.users}
        excelExport={assignedUsersExcelExport}
        rows={assignedUsersData}
        handleClearFilter={handleClearFilter}
        dataState={dataState}
      />
    ),
    [assignedUsers.users, assignedUsersData, assignedUsersExcelExport, dataState, handleClearFilter]
  );
  return (
    <main>
      <ViewCheckbookActions />
      <PeriodFilterPills />
      {dropDownPeriodValue && !hasTransactionData && (
        <div className="mb-3">
          <span className="bg-[#D9AA4F4D] p-1 px-2 text-[13px]">
            <FontAwesomeIcon icon={faTriangleExclamation} className="text-[#D9AA4F] mr-2" />
            <span className="font-medium">
              {t("Selected period doesn't have drill down data available")}
            </span>
          </span>
        </div>
      )}

      <GridWrapper
        id="viewPeriodCheckbookTable"
        className="view-period-checkbook min-h-[400px]"
        columns={periodViewColumns}
        dataSource={isEditMode ? editingbudgetPeriodDataWithErrors : periodViewData}
        total={periodViewTotal}
        dataState={periodCheckbookDataState}
        onDataStateChange={onCheckbookDataStateChange}
        height={`${gridHeight}px`}
        gridToolbarChildren={gridToolbarChildren}
        {...gridProps}
        loading={isGlCodesBudgetLoading || excelLoading}
        cellClick={handleCellFnClick}
        noRecordText="No records to display"
        onHeaderSelectionChange={onHeaderSelectionChange}
        onSelectionChange={onCheckbookSelectionChange}
        onItemChange={handleItemChange}
      />

      <Dialog
        width={balanceType === 'departments' ? '400px' : '80%'}
        id="modal"
        title={
          balanceType === 'departments'
            ? 'Departments'
            : checkbookBalanceTitle(
                dropDownPeriodValue || ({} as GLPeriodDropdownChildren),
                t,
                selectedGLBudget,
                balanceType
              )
        }
        open={openBalanceActivityModal}
        onClose={handleBalanceActivityModalClose}
      >
        {balanceType === 'holdAmount' && <CheckbookHoldAmountTable glBudget={selectedGLBudget} />}
        {balanceType === 'actualizedAmount' && (
          <CheckbookActualizedAmountTable glBudget={selectedGLBudget} />
        )}
        {balanceType === 'committedAmount' && (
          <CheckbookCommittedAmountTable glBudget={selectedGLBudget} />
        )}
        {balanceType === 'logAmount' && (
          <TransactionTable glCodeId={selectedGLBudget.id} glPeriodId={glPeriodIdByPeriod} />
        )}
        {balanceType === 'departments' && <DepartmentsTable departments={departments} />}
        <DialogActionsBar>
          <div className="flex justify-end">
            <Button onClick={handleBalanceActivityModalClose} color="info">
              {t('Close')}
            </Button>
          </div>
        </DialogActionsBar>
      </Dialog>

      <Dialog
        width="700px"
        id="modal"
        title={
          <div>
            <span>{t(`${assignedUsers.title} - Assigned Contacts`)}</span>
            <div>{t(`GL Code: ${assignedUsers.glCodeDescription}`)}</div>
          </div>
        }
        open={openUsersAssigned}
        onClose={handleAssignedUsersClose}
      >
        <div className="flex flex-col">
          <GridWrapper
            dataSource={assignedUsersData}
            columns={[
              {
                title: t('Contact'),
                format: '',
                filter: 'text',
                field: 'name',
                sortable: true,
                menu: !isColumnMenuDisabled,
                show: true,
              },
            ]}
            total={total}
            {...assignedUsersGridProps}
            dataState={dataState}
            onDataStateChange={onDataStateChange}
            noRecordText="No records to display"
            gridToolbarChildren={assignedUsersGridToolbarChildren}
            className="max-h-[50vh] hide-scroll-bar-approval-exemption overflow-y-auto"
          />
        </div>
        <DialogActionsBar>
          <div className="flex justify-end">
            <Button onClick={handleAssignedUsersClose} color="secondary">
              Close
            </Button>
          </div>
        </DialogActionsBar>
      </Dialog>

      <ManageGLCodeAttributesPanel />
    </main>
  );
};

export default ViewPeriodCheckbookTable;
